/**
 * COMP-36: Bracket Layout Utility
 * Calculates SVG layout positions for tournament brackets
 */

import { Tournament, Match, Participant, BracketLayout, RoundLayout, MatchLayout, ConnectorLayout } from '../types/tournament';

interface BracketLayoutConfig {
  matchWidth: number;
  matchHeight: number;
  roundSpacing: number;
  matchSpacing: number;
  padding: number;
}

const DEFAULT_CONFIG: BracketLayoutConfig = {
  matchWidth: 200,
  matchHeight: 80,
  roundSpacing: 250,
  matchSpacing: 100,
  padding: 50
};

export const calculateBracketLayout = (
  tournament: Tournament,
  matches: Match[],
  participants: Participant[],
  config: Partial<BracketLayoutConfig> = {}
): BracketLayout => {
  const layoutConfig = { ...DEFAULT_CONFIG, ...config };

  // Group matches by round
  const matchesByRound = groupMatchesByRound(matches);
  const rounds = Object.keys(matchesByRound).map(Number).sort((a, b) => a - b);

  if (rounds.length === 0) {
    return {
      width: layoutConfig.padding * 2,
      height: layoutConfig.padding * 2,
      rounds: [],
      matches: [],
      connectors: []
    };
  }

  // Calculate layout for each round
  const roundLayouts: RoundLayout[] = [];
  const matchLayouts: MatchLayout[] = [];
  const connectors: ConnectorLayout[] = [];

  let currentX = layoutConfig.padding;

  rounds.forEach((roundNumber, roundIndex) => {
    const roundMatches = matchesByRound[roundNumber];
    const roundHeight = calculateRoundHeight(roundMatches.length, layoutConfig);

    // Calculate Y positions for matches in this round
    const startY = layoutConfig.padding;
    const matchPositions = calculateMatchPositions(roundMatches.length, roundHeight, startY, layoutConfig);

    // Create round layout
    const roundLayout: RoundLayout = {
      name: getRoundName(roundNumber, rounds.length, tournament.bracketType),
      x: currentX + layoutConfig.matchWidth / 2,
      y: layoutConfig.padding - 30,
      matches: []
    };

    // Create match layouts for this round
    roundMatches.forEach((match, matchIndex) => {
      const matchLayout: MatchLayout = {
        match,
        position: {
          x: currentX,
          y: matchPositions[matchIndex]
        },
        participants: getMatchParticipants(match, participants)
      };

      matchLayouts.push(matchLayout);
      roundLayout.matches.push(matchLayout);
    });

    roundLayouts.push(roundLayout);

    // Create connectors to next round
    if (roundIndex < rounds.length - 1) {
      const nextRoundMatches = matchesByRound[rounds[roundIndex + 1]];
      const nextRoundX = currentX + layoutConfig.roundSpacing;
      const nextRoundPositions = calculateMatchPositions(
        nextRoundMatches.length,
        calculateRoundHeight(nextRoundMatches.length, layoutConfig),
        startY,
        layoutConfig
      );

      // Connect matches to next round
      roundMatches.forEach((_match, matchIndex) => {
        const nextMatchIndex = Math.floor(matchIndex / 2);
        if (nextMatchIndex < nextRoundPositions.length) {
          connectors.push({
            from: {
              x: currentX + layoutConfig.matchWidth,
              y: matchPositions[matchIndex] + layoutConfig.matchHeight / 2
            },
            to: {
              x: nextRoundX,
              y: nextRoundPositions[nextMatchIndex] + layoutConfig.matchHeight / 2
            }
          });
        }
      });
    }

    currentX += layoutConfig.roundSpacing;
  });

  // Calculate total dimensions
  const totalWidth = currentX - layoutConfig.roundSpacing + layoutConfig.matchWidth + layoutConfig.padding;
  const maxRoundHeight = Math.max(...rounds.map(r =>
    calculateRoundHeight(matchesByRound[r].length, layoutConfig)
  ));
  const totalHeight = maxRoundHeight + layoutConfig.padding * 2 + 50; // Extra space for round labels

  return {
    width: totalWidth,
    height: totalHeight,
    rounds: roundLayouts,
    matches: matchLayouts,
    connectors
  };
};

const groupMatchesByRound = (matches: Match[]): Record<number, Match[]> => {
  return matches.reduce((acc, match) => {
    if (!acc[match.round]) {
      acc[match.round] = [];
    }
    acc[match.round].push(match);
    return acc;
  }, {} as Record<number, Match[]>);
};

const calculateRoundHeight = (matchCount: number, config: BracketLayoutConfig): number => {
  return matchCount * config.matchHeight + (matchCount - 1) * config.matchSpacing;
};

const calculateMatchPositions = (
  matchCount: number,
  totalHeight: number,
  startY: number,
  config: BracketLayoutConfig
): number[] => {
  const positions: number[] = [];

  if (matchCount === 1) {
    positions.push(startY + totalHeight / 2 - config.matchHeight / 2);
  } else {
    for (let i = 0; i < matchCount; i++) {
      const y = startY + i * (config.matchHeight + config.matchSpacing);
      positions.push(y);
    }
  }

  return positions;
};

const getRoundName = (roundNumber: number, totalRounds: number, bracketType: Tournament['bracketType']): string => {
  if (bracketType === 'single_elimination') {
    if (roundNumber === totalRounds) return 'Final';
    if (roundNumber === totalRounds - 1) return 'Semi-Final';
    if (roundNumber === totalRounds - 2) return 'Quarter-Final';
    return `Round ${roundNumber}`;
  }

  if (bracketType === 'double_elimination') {
    // Double elimination has winners and losers brackets
    return `Round ${roundNumber}`;
  }

  return `Round ${roundNumber}`;
};

const getMatchParticipants = (match: Match, participants: Participant[]) => {
  const matchParticipants: Array<{
    participant: Participant;
    position: { x: number; y: number };
    isWinner: boolean;
  }> = [];

  if (match.participant1Id) {
    const participant1 = participants.find(p => p.id === match.participant1Id);
    if (participant1) {
      matchParticipants.push({
        participant: participant1,
        position: { x: 0, y: 0 }, // Relative to match position
        isWinner: match.winnerId === participant1.id
      });
    }
  }

  if (match.participant2Id) {
    const participant2 = participants.find(p => p.id === match.participant2Id);
    if (participant2) {
      matchParticipants.push({
        participant: participant2,
        position: { x: 0, y: 40 }, // Relative to match position
        isWinner: match.winnerId === participant2.id
      });
    }
  }

  return matchParticipants;
};

export default calculateBracketLayout;